import {greenColors, orangeColors} from '../../../../../../layouts/theme/designSystem';

interface Coordinate {
  x: number;
  y: number;
}

interface LineChartArgs {
  doc: any;
  width: number;
  height: number;
  dataSets: Array<number[]>;
  colors: string[];
  rowNames: string[] | number[];
  columnNames: string[];
}

export class LineChart {
  private readonly containerHeight = 140;
  private readonly containerWidth = 283;
  private readonly rowCount: number;
  private readonly xStart = 30; // Where graph begins.
  private readonly yPadding = 5;
  private readonly columnWidth = 15;
  private readonly graphHeight = this.containerHeight / 2 - this.yPadding; // chart takes up half the content.
  // actual graph width after accounting for the padding on either side.
  private readonly graphWidth = this.containerWidth - (this.xStart - this.columnWidth / 2) * 2;
  private readonly yAxisSpacing: number; // 6 data points on y axis

  private readonly xAxisSpacing: number;
  private readonly columnCount: number;
  private readonly width: number;
  private readonly height: number;
  // Color index corresponds to dataSets index for rendering colors.
  private readonly colors: string[];
  private readonly columnNames: string[];
  private readonly rowNames: string[] | number[];

  private readonly doc: any;
  private readonly dataSets: Array<number[]>;
  private coordinatePoints: Array<Coordinate[]> = [];

  constructor({doc, width, height, colors, columnNames, rowNames, dataSets}: LineChartArgs) {
    const calculateColumnSpacing = () => {
      const remainingSpace = this.graphWidth - this.columnCount * this.columnWidth;

      return remainingSpace / (this.columnCount - 1) + this.columnWidth;
    };

    const calculateRowSpacing = () => {
      const spacing = this.graphHeight / this.rowCount;
      const remainingSpace = this.graphHeight - this.yPadding - this.rowCount * spacing;

      return remainingSpace / (this.rowCount - 1) + spacing;
    };

    this.columnCount = columnNames.length;
    this.rowCount = rowNames.length;
    this.colors = colors;
    this.columnNames = columnNames;
    this.rowNames = rowNames;
    this.xAxisSpacing = calculateColumnSpacing();
    this.yAxisSpacing = calculateRowSpacing();
    this.doc = doc;
    this.width = width;
    this.height = height;
    this.dataSets = dataSets || [];
  }

  public create = (): void => {
    this.setDataCoordinates();

    // initializes background color and sets fontsize and color
    this.doc.rect(0, 0, this.width, this.height).fill(orangeColors[5]);
    this.doc.fontSize(8).fillColor(greenColors[700]);

    // Paints graph and graph layout
    this.insertRowNames();
    this.drawBarColumns();
    this.drawLineDivider();
    this.drawColumnText();

    // adds data
    this.drawPoints();
    this.drawConnectingLines();
  };

  private setDataCoordinates = () => {
    const yOffset = 1;
    const beginningYPosition = this.graphHeight + this.yPadding - yOffset;

    this.dataSets.forEach((dataSet) => {
      const coordinates: Coordinate[] = [];

      dataSet.forEach((value, index) => {
        coordinates.push({
          x: index === 0 ? this.xStart : this.xStart + this.xAxisSpacing * index,
          y: beginningYPosition - this.yAxisSpacing * value,
        });
      });
      this.coordinatePoints.push(coordinates);
    });
  };

  private insertRowNames = (): void => {
    const paddingX = 10;
    const names = this.rowNames.reverse();
    let spacingY = this.yPadding;

    names.forEach((name) => {
      this.doc.text(name, paddingX, spacingY);

      spacingY = spacingY + this.yAxisSpacing;
    });
  };

  private drawBarColumns = (): void => {
    let spacingX = this.xStart;
    let currColumn = 0;
    this.doc.lineWidth(this.columnWidth);

    while (currColumn < this.columnCount) {
      this.doc.moveTo(spacingX, this.yPadding);
      this.doc.lineTo(spacingX, this.containerHeight - this.yPadding).stroke(orangeColors[7]);

      spacingX = spacingX + this.xAxisSpacing;
      currColumn = currColumn + 1;
    }
  };

  private drawLineDivider = (): void => {
    const startingXPosition = this.xStart - this.columnWidth / 2;
    const endXPosition = this.containerWidth - startingXPosition;

    this.doc.moveTo(startingXPosition, this.graphHeight + this.yPadding);
    this.doc
      .lineTo(endXPosition, this.graphHeight + this.yPadding)
      .lineWidth(2)
      .stroke(orangeColors[3]);
  };

  // To create text that is sideways on the column is really hacky. The text is shoehorned in specifically for the size
  // of the graph that exists for the screening applicant pdf design.
  private drawColumnText = (): void => {
    const positionX = 78;
    let positionY = -46;
    let currColumn = 0;

    // Saves current state of the document
    this.doc.save();
    this.doc.fontSize(6);

    // Rotates document to allow writing text sideways
    this.doc.rotate(-90, {origin: [this.containerWidth / 2, this.containerHeight / 2]});

    while (currColumn < this.columnCount) {
      const title = this.columnNames[currColumn];

      if (title) {
        const [first, second] = title.split(' & ');
        this.doc.text(`${first} &`, positionX, positionY);
        this.doc.text(second, positionX, positionY + 6);
      }
      // I know this looks strange, but since we have rotated the document 90 degrees, the x-axis is now actually
      // the y-axis ( ˊᵕˋ )♡.°⑅.
      positionY = positionY + this.xAxisSpacing;
      currColumn = currColumn + 1;
    }
    // Restores the document to its old state. Essentially orienting the document appropriately again.
    this.doc.restore();
  };

  private drawPoints = (): void => {
    const radius = 3;

    this.coordinatePoints.forEach((set, setIndex) => {
      set.forEach((value) => {
        const color = this.colors[setIndex];

        this.doc.fillColor(color).circle(value.x, value.y, radius).fill();
      });
    });
  };

  private drawConnectingLines = (): void => {
    this.doc.lineWidth(1);
    this.coordinatePoints.forEach((set, setIndex) => {
      set.forEach((value, valueIndex) => {
        const color = this.colors[setIndex];

        if (valueIndex === 0) {
          this.doc.moveTo(value.x, value.y);
          return;
        }

        this.doc.lineTo(value.x, value.y);

        if (valueIndex === set.length - 1) {
          this.doc.stroke(color).opacity(0.8);
        }
      });
    });
  };
}
